import React, { useState } from "react";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import * as Dialog from "@radix-ui/react-dialog";
import { TodoItem } from "@/api/types";
import {
	deleteStateEntry,
	itemsState,
	listsState,
	setStateEntry,
	uiState,
	updateTaskCounts,
} from "@/states.ts";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select.tsx";
import { useSnapshot } from "valtio/react";
import { updateItem } from "@/api/offline.ts";
import { processQueue } from "@/api/queue.ts";
import { ColorPicker } from "@/components/ColorPicker.tsx";

export interface EditItemDialogProps {
	item: TodoItem;
}

const EditItemDialog: React.FC<EditItemDialogProps> = (props) => {
	const listsSnap = useSnapshot(listsState);
	const [text, setText] = useState(props.item.text);
	const [listId, setListId] = useState(props.item.list_id);
	const [link, setLink] = useState(props.item.link ?? "");
	const [note, setNote] = useState(props.item.description ?? "");
	const [color, setColor] = useState(props.item.color ?? "");

	const handleUpdate = () => {
		const fromListId = props.item.list_id;
		const toListId = listId;
		const hasChangedListId = (fromListId || toListId) && fromListId !== toListId;

		const newItem = { ...props.item, text, list_id: toListId, link, description: note, color };

		updateItem(props.item.list_id!, newItem).then((i) => {
			if (hasChangedListId && fromListId) {
				deleteStateEntry(itemsState.items, fromListId, i.item);
				updateTaskCounts(fromListId);
			}
			// Target items may not be loaded yet; getItems will fetch them on navigation.
			const targetItems = i.item.list_id ? itemsState.items[i.item.list_id] : undefined;
			if (targetItems) {
				setStateEntry(targetItems, i.item, "merge", true);
				if (hasChangedListId && toListId) {
					updateTaskCounts(toListId);
				}
			}
		});

		processQueue();
		handleClose();
	};

	const handleChangeList = (newListId: string) => {
		setListId(newListId);
	};

	const handleClose = () => {
		uiState.editingItem = null;
	};

	return (
		<>
			<Dialog.Root open={true}>
				<Dialog.Portal>
					<Dialog.Overlay className="DialogOverlay" />
					<Dialog.Content className="DialogContent">
						<div className={"p-4 flex flex-col space-y-2"}>
							<Dialog.Title className="DialogTitle">
								Edit Item
							</Dialog.Title>
							<Select
								onValueChange={handleChangeList}
								value={listId}
							>
								<SelectTrigger className="w-[180px]">
									<SelectValue placeholder="List" />
								</SelectTrigger>
								<SelectContent>
									{listsSnap.lists.map(
										(l) => (
											<SelectItem
												key={
													l.id
												}
												value={
													l.id
												}
											>
												<div
													className={
														"flex items-center gap-2"
													}
												>
													<div
														className={
															"rounded w-3 h-3"
														}
														style={{
															backgroundColor:
																l.color ||
																"#78ded9",
														}}
													/>
													{
														l.title
													}
												</div>
											</SelectItem>
										),
									)}
								</SelectContent>
							</Select>
							<div className={"flex items-center gap-4"}>
								<ColorPicker
									color={color}
									onChange={setColor}
								/>
								<Input
									placeholder={"Title"}
									value={text}
									className={
										"bg-secondary/30"
									}
									onChange={(e) =>
										setText(
											e.target
												.value,
										)
									}
								/>
							</div>
							<Input
								placeholder={"Link (optional)"}
								value={link}
								className={"bg-secondary/30"}
								onChange={(e) => setLink(e.target.value)}
							/>
							<textarea
								placeholder={"Note (optional)"}
								value={note}
								rows={3}
								className={
									"bg-secondary/30 rounded-md border border-input px-3 py-2 text-sm resize-none"
								}
								onChange={(e) => setNote(e.target.value)}
							/>
							<div
								className={
									"flex justify-between items-center pt-4"
								}
							>
								<div className="flex gap-4">
									<Button
										onClick={
											handleUpdate
										}
									>
										Update
									</Button>
									<Button
										variant={"ghost"}
										onClick={
											handleClose
										}
									>
										Cancel
									</Button>
								</div>
							</div>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</>
	);
};

export { EditItemDialog };
